import { useMemo, useState } from 'react';
import { useT } from '@hana/i18n';
import './screens.i18n';
import { IconCalendarEvent, IconChevronLeft, IconClock, IconId, IconPlus, IconCash, IconSearch, IconUserOff, IconUsers } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import type { Staff, StaffStatus } from './types';
import { filterStaff } from './staffList';
import { HrDetailPad } from './HrDetailPad';
import s from './compact.module.css';

// Mint-HR-iPad (khung 1: danh sách) · Mint-HR-Phone (khung 1). Chỉ đọc; nút ghi báo toast. Không lương / tiền:
// tab Lương và "Phiếu lương" chỉ là lối sang HANA Mee, không hiện số.

type Tab = 'staff' | 'time' | 'leave' | 'pay';
const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: 'staff', label: 'Hồ sơ' },
  { key: 'time', label: 'Chấm công' },
  { key: 'leave', label: 'Nghỉ phép' },
  { key: 'pay', label: 'Lương' },
];
const ST_TONE: Record<StaffStatus, string> = { 'Đang làm': s.tOk ?? '', 'Nghỉ phép': s.tWarn ?? '', 'Thử việc': s.tPri ?? '' };
const MEE_ROWS = [
  { k: 'Chấm công', to: '/mee#time', icon: IconClock },
  { k: 'Đơn từ', to: '/mee#leave', icon: IconCalendarEvent },
  { k: 'Phiếu lương', to: '/mee#pay', icon: IconCash },
] as const;

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

/** Danh sách iPad của board: 10 người, đúng thứ tự (script board `STAFF`). */
const BOARD_ORDER = ['DAVID', 'HANHLTM', 'PHONGNT', 'PhuongHT', 'MAITTN', 'TUANNT', 'THANHTTK', 'NhungTT', 'LAN', 'PHILIP'] as const;
const seatsOf = (x: Staff) => x.seats.map((h) => h.code).join(' + ');

export function HrCompact({ phone }: { phone: boolean }) {
  const t = useT();
  const toast = useToast();
  const { data, error, isLoading, isError, refetch } = useList('hrStaff', { limit: 100 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('staff');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const all = useMemo(() => BOARD_ORDER.flatMap((id) => (data?.items ?? []).filter((x) => x.id === id)), [data]);
  const rows = useMemo(() => filterStaff(all, { q, dept: 'Tất cả' }), [all, q]);

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (!phone && all.length === 0) state = 'empty';

  const noWrite = () => toast({ msg: t('hr.s.noWrite') });
  const pickTab = (k: Tab) => (k === 'staff' ? setTab(k) : toast({ msg: 'Tab này chưa mở ở bản này' }));
  const absent = () => toast({ msg: 'Uỷ quyền ghế khi vắng chưa mở ở bản này' });

  if (phone) {
    return (
      <Page>
        <PageHeader
          title="Nhân sự"
          lead={<a className={s.back} href="/more" aria-label="Quay lại Thêm"><IconChevronLeft size={22} aria-hidden="true" /></a>}
        />
        <StateView state={state} icon={<IconUsers size={26} stroke={1.8} />} action={state === 'error' ? <Button onClick={() => void refetch()}>{t('hr.s.retry')}</Button> : undefined} skeleton={<Skeleton rows={4} />}>
          <div className={s.phone}>
            <section className={s.mee} aria-label="HANA Mee">
              <div className={s.meeHd}>
                <span className={s.meeIc}><IconId size={24} stroke={1.9} aria-hidden="true" /></span>
                <div><b>HANA Mee</b><div>{t('hr.s.meeSub')}</div></div>
              </div>
              <a className={s.meeGo} href="/mee">{t('hr.s.openMee')}</a>
            </section>
            <nav className={s.meeNav} aria-label="Mục trong HANA Mee">
              {MEE_ROWS.map((r) => (
                <a key={r.k} href={r.to}>
                  <r.icon size={20} stroke={1.9} aria-hidden="true" />
                  <span>{r.k}</span>
                  <span aria-hidden="true">↗</span>
                </a>
              ))}
            </nav>
            <button type="button" className={s.absent} onClick={absent}><IconUserOff size={16} stroke={1.8} aria-hidden="true" />{t('hr.s.absentToday')}</button>
          </div>
        </StateView>
      </Page>
    );
  }

  const picked = sel ? all.find((x) => x.id === sel) : undefined;
  if (picked && state === 'ready') return <Page><HrDetailPad staff={picked} onBack={() => setSel(null)} /></Page>;

  return (
    <Page>
      <PageHeader
        title="Nhân sự"
        tabs={<Segmented label="Mục" value={tab} onChange={pickTab} items={TABS} />}
        actions={
          <>
            <HeaderTool label="Vắng hôm nay" icon={IconUserOff} onClick={absent} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('hr.s.leaveReq')}</Button>
          </>
        }
      />
      <StateView
        state={state}
        title={state === 'empty' ? 'Chưa có nhân sự trong phòng này' : undefined}
        desc={state === 'empty' ? 'Thêm hồ sơ từ Tuyển dụng (Nhận việc) hoặc trên máy tính.' : undefined}
        icon={state === 'empty' ? <IconUsers size={26} stroke={1.8} /> : undefined}
        action={state === 'error' ? <Button onClick={() => void refetch()}>{t('hr.s.retry')}</Button> : undefined}
        skeleton={<Skeleton rows={8} />}
      >
        <section className={s.list} aria-label="Danh sách nhân sự">
          <label className={s.search}>
            <IconSearch size={18} stroke={2.2} aria-hidden="true" />
            <input aria-label="Tìm nhân sự" placeholder="Tên, ghế…" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className={s.rows}>
            {rows.map((x) => (
              <button type="button" key={x.id} className={s.row} onClick={() => setSel(x.id)}>
                <span aria-hidden="true" className={s.av}>{x.id.slice(0, 2)}</span>
                <span className={s.who}><b>{x.id} · {x.name}</b><span>{seatsOf(x)} · {x.dept}</span></span>
                <span className={`${s.st} ${ST_TONE[x.status]}`}>{x.status}</span>
              </button>
            ))}
          </div>
        </section>
      </StateView>
    </Page>
  );
}
